import { resolvedPosFind, resolvedPosFindNode } from '@common/prosemirror/model/resolved-pos';
import { getWordAt } from '@common/util/string';
import { Node, NodeRange, NodeType, ResolvedPos } from 'prosemirror-model';

export function getNearestAncestorRange($pos: ResolvedPos, nodeType: NodeType): NodeRange {
  const depthInfo = resolvedPosFind($pos, node => node.type === nodeType);

  if (depthInfo) {
    return new NodeRange($pos.doc.resolve($pos.start(depthInfo.depth)), $pos.doc.resolve($pos.end(depthInfo.depth)), depthInfo.depth);
  }
}

// Gets the range for the word at a given position in a text node
export function getWordRange($pos: ResolvedPos): NodeRange {
  const parent = $pos.parent;

  if (parent.inlineContent) {
    const childNode = parent.nodeAt($pos.parentOffset - 1 >= 0 ? $pos.parentOffset - 1 : 0);
    if (childNode && childNode.isText) {
      // prosemirror for whatever reason sets textOffset to 0 if you are at the end of a node, since we know we are at the
      // end the size of the node is now the correct position.
      const textOffset = $pos.textOffset === 0 && $pos.parentOffset !== 0 ? childNode.text.length : $pos.textOffset;
      const wordInfo = getWordAt(childNode.text, textOffset);

      if (wordInfo) {
        return new NodeRange($pos.doc.resolve($pos.pos - wordInfo.startDistance), $pos.doc.resolve($pos.pos + wordInfo.endDistance), $pos.depth);
      }
    }
  }
}

// Returns the range from $pos1 to $pos2 if the positions share the same parent and the parent allows inline content
export function getInlineRange($pos1: ResolvedPos, $pos2: ResolvedPos): NodeRange {
  if ($pos2.pos < $pos1.pos) {
    return getInlineRange($pos2, $pos1);
  }

  if ($pos1.parent.inlineContent && $pos1.parent === $pos2.parent) {
    return new NodeRange($pos1, $pos2, $pos1.depth);
  }
}

// Gets the text ranges between $pos1 and $pos2. If the content between the two positions contains multiple block nodes then a text range is returned for each block node.
export function getTextRanges(from: number, to: number, doc: Node, nodeType?: NodeType): NodeRange[] {
  const ranges = [];

  doc.nodesBetween(from, to, (node: Node, pos: number, parent: Node) => {
    if (node.isText) {
      const $start = doc.resolve(Math.max(pos, from));
      const $end = doc.resolve(Math.min(pos + node.nodeSize, to));
      const depth = $start.depth;

      // If the range should be limited to a specific node type and the that node type is an ancestor of this node
      if (!nodeType || resolvedPosFindNode($start, node => node.type === nodeType)) {
        // START - The below is just for logging
        // let slice = doc.slice($start.pos, $end.pos);
        // if (slice.content.childCount === 1) {
        //     slice = slice.content.firstChild;

        //     if (slice.type.name === 'text') {
        //         slice = '"' + slice.text + '"';
        //     }
        // }

        // console.log('getTextRanges.isText', '<' + node.type.name + '>', '[' + $start.pos + ', ' + $end.pos + ', ' + depth + ']' + ' in <' + parent.type.name + '>', 'SLICE', slice);
        // END - just for logging


        ranges.push(new NodeRange($start, $end, depth));
      }
    }
  });

  // console.log('getTextRanges.ranges', ranges);

  return ranges;
}

export function getInlineNodeRange($pos: ResolvedPos): NodeRange {
  if ($pos.parent.inlineContent) {
    return new NodeRange($pos.doc.resolve($pos.start()), $pos.doc.resolve($pos.end()), $pos.depth);
  }
}

/**
 * Gets the interior range of the given node where 'pos' is the starting position of the node.
 * This function is useful for the node unbinding.
 */
export function nodeInterior(doc: Node, node: Node, pos: number): NodeRange {
  const $from = doc.resolve(pos + 1);
  const $to = doc.resolve(pos + node.nodeSize - 1);
  // in case of childless nodes restore order of positions so the range will contain the whole node
  if ($to.pos < $from.pos) {
    return new NodeRange($to, $from, $to.depth);
  } else {
    return new NodeRange($from, $to, $from.depth);
  }
}
